import EditableWrapper, { InputType } from "@/components/ui/editable-wrapper";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import {
  ManualAssistantMessage,
  useAiContextStore,
} from "@/store/zustand/aiContextStore";
import { useProjectCreateStore } from "@/store/zustand/projectCreateStore";
import { generateJsonStream } from "@/utils/ai/utils";
import {
  fixJson,
  getAxiosErorrMessage,
  getUnexpiredToken,
  logAxiosError,
  useCanvasId,
} from "@/utils/utils";
import _ from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { useEditPrd } from "./hooks/useEditPrd";
import { SyncedTipTapEditor } from "./SyncedTipTapEditor";
import MermaidTab from "./Mermaid";
import { useSearchParams } from "react-router";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import { useSession } from "@clerk/clerk-react";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { FeaturePRD } from "@/types/issues";
import { useSupabaseStore, Message } from "@/store/zustand/supabaseStore";
import { PRD_CONSTANTS } from "./constants";
import { cn } from "@/lib/utils";
import useLayoutStore, {
  LayoutMode,
  LeftTab,
} from "@/store/zustand/layoutStore";
import { X } from "lucide-react";
import { useCreateMessageForChat } from "../chat/utils/messages";

export type PRDVisualContext = {
  text: string;
  url?: string;
  type: string;
  name?: string;
}[];

export type PRDDesignSuggestion = {
  id: string;
  path: string;
  type: "PAGE" | "UI_COMPONENT";
  description: string;
  layout: string;
  instruction?: string;
  interactions?: {
    [key: string]: string;
  };
  estimated_wireframe_dimensions?: {
    width: number;
    height: number;
    fin: "y";
  };
  stories?: {
    description: string;
    states: {
      name?: string;
      description?: string;
    }[];
    instruction?: string;
  };
  previouslyDefined?: boolean;
  components: PRDDesignSuggestion[];
};

export type PRDDesignSuggestions = {
  pageOrComponent: PRDDesignSuggestion;
};

const THROTTLE_DELAY_MS = 1000;

const throttledUpdatePrd = _.throttle(
  async (
    supabaseClient: any,
    newPrd: string,
    canvasId: string,
    featurePrd: FeaturePRD,
    updateFeaturePRD: any,
  ) => {
    updateFeaturePRD(supabaseClient, {
      id: featurePrd.id,
      canvas_id: canvasId,
      prd: newPrd,
    });
  },
  THROTTLE_DELAY_MS,
  { leading: true, trailing: true },
);

const PRDTab = React.memo(() => {
  const canvasId = useCanvasId();

  const [searchParams, setSearchParams] = useSearchParams();
  const generatePRDFlow: boolean = searchParams.get("generate-prd") === "t";

  const setNewLayout = useLayoutStore((state) => state.setNewLayout);

  const createFeaturePRD = useIssuesStore((state) => state.createFeaturePRD);
  const updateFeaturePRD = useIssuesStore((state) => state.updateFeaturePRD);
  const currentIssue = useIssuesStore(
    (state) => state.canvasIdToIssue[canvasId],
  );

  const onEditPrd = useEditPrd();

  const generateText = useProjectCreateStore((state) => state.generateText);
  const generateAttachedFiles = useProjectCreateStore(
    (state) => state.generateAttachedFiles,
  );
  const stylesBuilderInstructions = useProjectCreateStore(
    (state) => state.stylesBuilderInstructions,
  );

  const { session } = useSession();

  const setTempAssistantMessage = useAiContextStore(
    (state) => state.setTempAssistantMessage,
  );
  const setAddManualMessage = useAiContextStore(
    (state) => state.setAddManualMessage,
  );
  const refreshPlanInfo = usePlanInfoStore((state) => state.refreshPlanInfo);

  const [generatingPrd, setGeneratingPrd] = useState(false);
  const [abortController, setAbortController] = useState(new AbortController());

  const stopAiTrigger = useAiActionStore((state) => state.stopAiTrigger);

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const onDbSnapshot = useSupabaseStore((state) => state.onDbSnapshot);

  const { createUserMessage } = useCreateMessageForChat();

  const persistMessagesToDb = async (newMessages: Message[]) => {
    onDbSnapshot("aiChatMessages", {
      newItems: newMessages,
    });
  };

  useEffect(() => {
    if (stopAiTrigger && abortController) {
      abortController.abort();
      // @ts-expect-error TS2345
      setAbortController(null);
    }
  }, [stopAiTrigger]);

  /**
   * Create the prd if it doesn't exist
   */
  useEffect(() => {
    if (!supabaseClient) return;

    const createPrd = async () => {
      const newPrd: any = {
        id: uuidv4().toString(),
        canvas_id: canvasId,
      };

      createFeaturePRD(supabaseClient, newPrd);
    };

    if (currentIssue && !currentIssue.featurePrd) {
      createPrd();
    }
  }, [currentIssue, supabaseClient]);

  const generatePRD = async () => {
    setGeneratingPrd(true);

    useProjectCreateStore.getState().setIsFtueGenerating(true);

    // Create and persist user message with the original prompt and attached files
    const userMessage = createUserMessage({
      userPrompt: generateText || "Create a PRD for this project",
      attachedFiles: generateAttachedFiles ?? undefined,
      chatOnlyMode: false,
    });

    await persistMessagesToDb([userMessage]);

    const messageText = "Generating PRD...";
    const progressMessage: ManualAssistantMessage = {
      id: uuidv4().toString(),
      text: messageText,
      progressItems: [
        {
          title: "New PRD",
          loading: true,
        },
      ],
    };
    setTempAssistantMessage(progressMessage);

    /**
     * Update based on the current response format returned from the vellum workflow
     */
    const updatePRD = (newPrd: string) => {
      if (!currentIssue) return;

      const featurePrd = currentIssue.featurePrd ?? {
        id: uuidv4().toString(),
        canvas_id: canvasId,
      };

      throttledUpdatePrd(
        supabaseClient,
        newPrd,
        canvasId,
        featurePrd,
        updateFeaturePRD,
      );
    };

    try {
      const url = `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/canvases/${canvasId}/ai/vercel-ai/aiGeneratePrd`;

      const fullPrompt = stylesBuilderInstructions
        ? `${generateText}\n\n${stylesBuilderInstructions}`
        : generateText;

      const body = {
        visual_context:
          generateAttachedFiles && generateAttachedFiles.length > 0
            ? generateAttachedFiles.map((file) => ({
                url: file.url,
                mimeType: file.mimeType,
                type: file.type,
                name: file.name,
              }))
            : undefined,
        generateText: fullPrompt,
      };

      const newAbortController = new AbortController();
      setAbortController(newAbortController);

      const supabaseJWT = await getUnexpiredToken(session);

      const stream = await generateJsonStream(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal: newAbortController.signal,
      });

      let res = null;
      let partialResult = "";

      for await (const chunk of stream) {
        if (chunk.status === -1 && chunk.message === "intermediate") {
          const parsedChunk = JSON.parse(chunk.data);

          if (parsedChunk.type === "text-delta") {
            partialResult += parsedChunk.chunk;
          }

          // Only update if we have valid PRD content (skip empty updates)
          const fixedJson = fixJson(partialResult);
          const prdContent = fixedJson.fixedObj?.prd;

          if (prdContent && prdContent.trim().length > 0) {
            updatePRD(prdContent);
          }
        } else {
          res = chunk;
          break;
        }
      }

      // @ts-expect-error TS18047
      if (res.status >= 400) {
        console.error({
          // @ts-expect-error TS18047
          msg: res.message,
          // @ts-expect-error TS18047
          internalErrorStr: res.internalErrorStr,
        });
        // @ts-expect-error TS18047
        throw new Error(res.message);
      }

      try {
        // @ts-expect-error TS18047
        const finalPrd = res.data.steps[0].text;

        const fixedJson = fixJson(finalPrd);
        const prdContent = fixedJson.fixedObj?.prd;

        if (prdContent && prdContent.trim().length > 0) {
          updatePRD(prdContent);
        } else {
          console.error("Final PRD parsing failed - no content found");
          toast.error("Failed to parse PRD from AI response");
        }
      } catch (e) {
        console.error("Error parsing final PRD:", e);
        toast.error("Failed to save PRD");
      }

      // @ts-expect-error TS2345
      setAbortController(null);
      setTempAssistantMessage(null);
      setAddManualMessage({
        ...progressMessage,
        progressItems: progressMessage.progressItems?.map((item) => ({
          ...item,
          succeeded: true,
        })),
      });
      toast.success("Generated PRD");
      setGeneratingPrd(false);
      return true;
    } catch (err) {
      logAxiosError("Error generating PRD", err);
      if (err.name !== "AbortError") {
        toast.error(`Error generating PRD: ` + getAxiosErorrMessage(err));
      }
      // @ts-expect-error TS2345
      setAbortController(null);
      setTempAssistantMessage(null);
      setAddManualMessage({
        ...progressMessage,
        progressItems: progressMessage.progressItems?.map((item) => ({
          ...item,
          failed: true,
        })),
      });
      setGeneratingPrd(false);
      useProjectCreateStore.getState().setIsFtueGenerating(false);
      return false;
    } finally {
      refreshPlanInfo(session);
    }
  };

  const setFromIdeaRef = useRef(false);

  useEffect(() => {
    if (setFromIdeaRef.current) return;

    if (!session) return;

    if (
      generatePRDFlow &&
      currentIssue &&
      generateText &&
      !currentIssue.featurePrd?.prd
    ) {
      setFromIdeaRef.current = true;
      generatePRD()
        .then((success) => {
          setSearchParams(
            (prev) => {
              const newParams = {
                ...Object.fromEntries(prev.entries()),
              };
              if (success) {
                newParams["generate-mermaid"] = "t";
              }
              delete newParams["generate-prd"];
              return newParams;
            },
            {
              preventScrollReset: true,
            },
          );
        })
        .catch((err) => {
          console.error("error generating PRD", err);
        });
    }
  }, [generatePRDFlow, currentIssue, Boolean(session)]);

  const onEditTitle = useCallback(
    async (newValue: string) => {
      onEditPrd("title", newValue, true);
    },
    [onEditPrd],
  );

  return (
    <div className="w-full h-full bottom-0 relative flex flex-col border-l border-r bg-secondary-100">
      <div className="absolute top-1 right-1 z-10">
        <div
          onClick={() => {
            setNewLayout(LeftTab.AIChat, LayoutMode.Design);
          }}
        >
          <X className="w-4 h-4 cursor-pointer text-secondary-800 hover:text-secondary-900 mr-1 mt-1" />
        </div>
      </div>

      <div className="flex flex-col gap-2 p-4 px-0 h-full overflow-y-scroll hide-scrollbar">
        <div className="flex flex-col justify-between items-start pb-2 px-6 pr-8">
          <EditableWrapper
            value={currentIssue?.featurePrd?.title || "Feature Title"}
            onEdit={onEditTitle}
            linearMode={true}
            type={InputType.TEXTAREA}
            maxLength={PRD_CONSTANTS.MAX_TITLE_LENGTH}
          >
            <div className="text-sm font-semibold">
              {currentIssue?.featurePrd?.title || "Feature Title"}
            </div>
          </EditableWrapper>
        </div>
        <div className="py-4 px-6 m-0!">
          <SyncedTipTapEditor
            valueInSupabase={currentIssue?.featurePrd?.prd || ""}
            onUpdateValueInSupabase={(newValue) =>
              onEditPrd("prd", newValue, true)
            }
            placeholder="Write your PRD here..."
          />
        </div>
        <MermaidTab />
      </div>
    </div>
  );
});

export default PRDTab;
